import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import * as BsIcons from 'react-icons/bs';
import SafeIcon from '../../common/SafeIcon';

const { FiClock, FiUser, FiArrowLeft, FiShare2 } = FiIcons;
const { BsBrain, BsSunshine, BsLightningCharge } = BsIcons;

const BdnfBreakthrough = () => {
  return (
    <div className="pt-24 pb-20 bg-white">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Navigation */}
        <Link to="/resources" className="inline-flex items-center text-slate-500 hover:text-indigo-600 mb-8 transition-colors font-medium">
          <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Library
        </Link>

        {/* Article Header */}
        <header className="mb-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <span className="inline-block px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-bold uppercase tracking-wider">
              Neuroscience
            </span>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight">
              The BDNF Breakthrough: Why Scientists Call It "Miracle-Gro" for Your Brain
            </h1>
            <div className="flex items-center justify-between border-b border-slate-100 pb-8">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">
                  SV
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">Sarah Vance</p>
                  <p className="text-xs text-slate-500 flex items-center gap-2">
                    <SafeIcon icon={FiClock} /> 6 min read
                  </p>
                </div>
              </div>
              <button className="p-2 text-slate-400 hover:text-indigo-600 transition-colors">
                <SafeIcon icon={FiShare2} className="text-xl" />
              </button>
            </div>
          </motion.div>
        </header>

        {/* Featured Image */}
        <div className="rounded-3xl overflow-hidden mb-12 shadow-xl">
          <img 
            src="https://cdn1.sharemyimage.com/smi/2026/01/01/3d-medical-background-with-male-head-brain-dna-strands.jpg" 
            alt="BDNF Visualization" 
            className="w-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="prose prose-lg prose-slate max-w-none">
          <p className="lead text-xl text-slate-600 mb-8">
            Imagine a protein that could help your brain grow new cells, repair dying ones, and strengthen the connections that allow you to learn and remember. That protein exists, and it's called <strong>Brain-Derived Neurotrophic Factor (BDNF)</strong>.
          </p>

          <h3>What Exactly is BDNF?</h3>
          <p>
            Discovered in 1982, BDNF is a protein encoded by the BDNF gene. In the neuroscience community, it's often referred to as "Miracle-Gro for the brain." It is active in the hippocampus, cortex, and basal forebrain—areas vital to learning, memory, and higher thinking.
          </p>
          <p>
            Low levels of BDNF have been linked to a variety of cognitive issues, including brain fog, poor memory retention, and accelerated cognitive decline. Conversely, higher levels are associated with faster learning, better mood regulation, and improved neuroplasticity.
          </p>

          <div className="my-10 p-8 bg-indigo-50 rounded-2xl border border-indigo-100">
            <h4 className="flex items-center gap-2 text-indigo-900 font-bold mb-4">
              <SafeIcon icon={BsBrain} /> The 3 Key Roles of BDNF
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                <span><strong>Neuroprotection:</strong> It protects existing neurons from damage and stress.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                <span><strong>Neurogenesis:</strong> It encourages the growth of new neurons (brain cells).</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                <span><strong>Synaptic Plasticity:</strong> It strengthens the connections (synapses) between neurons.</span>
              </li>
            </ul>
          </div>

          <h3>Why Your BDNF Might Be Low</h3>
          <p>
            Modern lifestyles are essentially "anti-BDNF." Chronic stress, high sugar intake, lack of sleep, and social isolation all contribute to lowering BDNF levels. The stress hormone cortisol, in particular, is a known BDNF inhibitor. When you are stressed, your brain literally stops prioritizing repair and growth.
          </p>

          <h3>4 Proven Ways to Boost BDNF Naturally</h3>
          
          <h4>1. High-Intensity Exercise</h4>
          <p>
            Brief bursts of intense activity have been shown to be more effective at releasing BDNF than continuous moderate exercise. Sprints or HIIT workouts trigger a recovery response that floods the brain with reparative proteins.
          </p>

          <h4>2. Intermittent Fasting</h4>
          <p>
            Evolutionarily, hunger signaled to the brain that it needed to be sharp to find food. Fasting places a mild stress on the brain cells, which responds by producing more BDNF to ensure survival.
          </p>

          <h4>3. Sunlight and Vitamin D</h4>
          <p>
            There is a direct correlation between Vitamin D levels and BDNF. Getting 15-20 minutes of sun exposure (or supplementing appropriately) supports the genetic expression of BDNF.
          </p>

          <h4>4. Deep Rest & Neural Entrainment</h4>
          <p>
            Deep sleep is when the brain flushes out toxins. Recently, researchers have also looked at <strong>Gamma brainwave activity</strong> (40Hz) as a potential trigger for cleaning mechanism and neural synchronization. Tools like <Link to="/review/the-brain-song" className="text-indigo-600 font-bold hover:underline">The Brain Song</Link> utilize audio frequencies to help induce these states without years of meditation practice.
          </p>

          <hr className="my-12 border-slate-200" />

          <div className="bg-slate-900 text-white p-8 rounded-3xl text-center">
            <h3 className="text-white mb-4">Ready to Optimize Your Brain?</h3>
            <p className="text-slate-300 mb-6">
              Learn how audio entrainment can be the easiest way to support your daily brain health routine.
            </p>
            <Link 
              to="/review/the-brain-song"
              className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-xl font-bold transition-colors"
            >
              Read Our Review
            </Link>
          </div>

        </div>
      </article>
    </div>
  );
};

export default BdnfBreakthrough;